import React from "react";
import Card from "@mui/material/Card";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import ArgonButton from "components/ArgonButton";
import {
  FormControl,
  Select,
  LinearProgress,
  MenuItem,
  InputLabel,
  Chip,
  Button,
} from "@mui/material";
import { Person, Refresh, FilterList } from "@mui/icons-material";
// MUI

import breakpoints from "assets/theme/base/breakpoints";

// Data
import TutorsPayrollViewStudentTableData from "Views/Activities/TutorsPayroll/data/tutorsPayrollViewStudentTableData";

import CreditMappingViewTableData from "Views/Activities/CreditMapping/data/creditMappingViewTableData";
import CreditMappingViewCreditTableData from "Views/Activities/CreditMapping/data/creditMappingViewCreditTableData";
import TutorsParollViewPaymentOrderTableData from "Views/Activities/TutorsPayroll/data/tutorsPayrollViewPaymentOrderTableData";
import PropTypes from "prop-types";

// import ReferralTableData from "Views/Activities/Referral/data/ReferralTableData"
import { useState, useEffect } from "react";
import { set } from "date-fns";
import Dropdown from "components/CustomDropdown/Dropdown";
import ArgonInput from "components/ArgonInput";

function payroll({ classData, payrollCycles = [] }) {
  const [selectedCycle, setSelectedCycle] = useState("");
  const [currentPayrollData, setCurrentPayrollData] = useState(null);

  // Set default payroll data when payrollCycles are loaded
  useEffect(() => {
    if (payrollCycles && payrollCycles.length > 0) {
      // Find currently active/running cycle (ACTIVE, PENDING, APPROVED status)
      const activeCycle = payrollCycles.find((cycle) =>
        ["ACTIVE", "PENDING", "APPROVED", "PROCESSING"].includes(cycle.status)
      );

      // Set default cycle (active cycle or first cycle)
      const defaultCycle = activeCycle || payrollCycles[0];
      setCurrentPayrollData(defaultCycle);
      setSelectedCycle(defaultCycle._id);
    }
  }, [payrollCycles]);

  // Extract data from currentPayrollData (tutor payroll cycle data) instead of classData
  const tutorName =
    currentPayrollData?.tutorId?.fullName || currentPayrollData?.tutorId?.name || "N/A";
  const tutorEmail = currentPayrollData?.tutorId?.email || "";
  const tutorPhone = currentPayrollData?.tutorId?.phone || "";
  const tutorStatus = currentPayrollData?.status || "PENDING";
  const cycleType = currentPayrollData?.cycleType || "MONTHLY";

  // Calculate amounts from payroll data
  const totalAmount = currentPayrollData?.calculatedAmount || 0;
  const bonusAmount = currentPayrollData?.bonusAmount || 0;
  const deductionAmount = currentPayrollData?.deductionAmount || 0;
  const finalAmount = totalAmount + bonusAmount - deductionAmount;

  // Calculate tutor pay related values
  const tutorPayRate = currentPayrollData?.financeId?.tutorPayRate || 0;
  const totalClasses = currentPayrollData?.totalClasses || 0;
  const completedClasses = currentPayrollData?.completedClasses || 0;
  const totalMinutes = currentPayrollData?.totalMinutes || 0;
  const classDuration = currentPayrollData?.financeId?.students?.[0]?.preferredTimeDuration || 60;

  // Expected total tutor pay for all classes in cycle
  const expectedTotalTutorPay = (tutorPayRate * totalClasses * classDuration) / 60;

  // Tutor pay earned so far (based on completed classes)
  const tutorPayEarned =
    completedClasses > 0 ? (tutorPayRate * completedClasses * classDuration) / 60 : 0;

  // Progress percentage based on completed classes
  const classProgressPercentage =
    totalClasses > 0 ? Math.round((completedClasses / totalClasses) * 100) : 0;

  // Fee charged to parent per hour
  const feeChargePerHour = currentPayrollData?.financeId?.totalAmount
    ? currentPayrollData.financeId.totalAmount / (totalMinutes / 60 || 1)
    : 0;

  // Monthly charge (total fee charged to parent)
  const monthlyCharge = currentPayrollData?.financeId?.totalAmount || 0;

  // Calculate profit (Monthly Charge - Expected Total Tutor Pay)
  const monthlyProfit = monthlyCharge - expectedTotalTutorPay;

  // Get status color based on status
  const getStatusColor = () => {
    switch (tutorStatus) {
      case "PAID":
        return "linear-gradient(135deg, #43a047, #26a69a)";
      case "APPROVED":
        return "linear-gradient(135deg, #2196f3, #1976d2)";
      case "PENDING":
        return "linear-gradient(135deg, #f59e0b, #f97316)";
      case "REJECTED":
      case "FAILED":
        return "linear-gradient(135deg, #ef4444, #dc2626)";
      default:
        return "linear-gradient(135deg, #6b7280, #4b5563)";
    }
  };

  // Build dynamic student table data from financeId in payroll data
  const buildStudentTableData = () => {
    // Use classData.students if available (has subjects), otherwise fallback to financeId.students
    const studentsSource = classData?.students || currentPayrollData?.financeId?.students;

    if (!studentsSource || studentsSource.length === 0) {
      return { columns: [], rows: [], totalStudents: 0 };
    }

    const columns = [
      { name: "sno", align: "center" },
      { name: "student name", align: "left" },
      { name: "school name", align: "left" },
      { name: "board", align: "center" },
      { name: "class", align: "center" },
      { name: "subject", align: "center" },
      { name: "date of birth", align: "center" },
      { name: "location city", align: "center" },
    ];

    const students = studentsSource;

    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    };

    const rows = students.map((student, index) => ({
      sno: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {index + 1}
        </ArgonTypography>
      ),
      "student name": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {student.name || student.fullName || "N/A"}
        </ArgonTypography>
      ),
      "school name": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {student.schoolCollegeName || student.schoolName || "N/A"}
        </ArgonTypography>
      ),
      class: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {student.class?.name || student.class || "N/A"}
        </ArgonTypography>
      ),
      subject: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {student.subjects && student.subjects.length > 0
            ? student.subjects.map((sub) => sub.name || sub).join(", ")
            : "N/A"}
        </ArgonTypography>
      ),
      board: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {student.board?.name || student.board || "N/A"}
        </ArgonTypography>
      ),
      "date of birth": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {formatDate(student.dob)}
        </ArgonTypography>
      ),
      "location city": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {classData?.parentId?.city || currentPayrollData?.financeId?.parentId?.city || "N/A"}
        </ArgonTypography>
      ),
    }));

    return { columns, rows, totalStudents: students.length };
  };

  // Build credit details table data
  const buildCreditDetailsTableData = () => {
    const columns = [
      { name: "Cycle Name", align: "left" },
      { name: "Total Classes", align: "center" },
      { name: "Completed Classes", align: "center" },
      { name: "Status", align: "center" },
    ];

    const rows = payrollCycles.map((cycle, index) => ({
      "Cycle Name": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          Cycle {cycle.cycleNumber || index + 1}
        </ArgonTypography>
      ),
      "Total Classes": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {cycle.totalClasses || 0}
        </ArgonTypography>
      ),
      "Completed Classes": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {cycle.completedClasses || 0}
        </ArgonTypography>
      ),
      Status: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {cycle.status || "N/A"}
        </ArgonTypography>
      ),
    }));

    return { columns, rows };
  };

  const { columns, rows, totalStudents } = buildStudentTableData();
  const { columns: creditColumns, rows: creditRows } = buildCreditDetailsTableData();
  const { columns: columns2, rows: rows2 } = TutorsParollViewPaymentOrderTableData;

  const [cycle, setCycle] = useState("");

  // Handle cycle selection change
  const handleCycleChange = (e) => {
    const cycleId = e.target.value;
    setCycle(cycleId);
    setSelectedCycle(cycleId);
    const selectedCycleData = payrollCycles.find((c) => c._id === cycleId);
    if (selectedCycleData) {
      setCurrentPayrollData(selectedCycleData);
    }
  };

  return (
    <ArgonBox mb={3}>
      <Card
        sx={{
          mt: 3,
          borderRadius: "20px",
          backgroundColor: "white",
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          border: "1px solid #f0f0f0",
          p: 3,
        }}
      >
        {/* Title */}
        <ArgonBox mb={3}>
          <ArgonTypography variant="h5" fontWeight="bold" color="dark">
            {"Tutor's Payroll Management"}
          </ArgonTypography>
          <ArgonTypography variant="body2" color="text">
            {"Manage tutor status, cycle, and payroll details"}
          </ArgonTypography>
        </ArgonBox>

        {/* Tutor Info Row */}
        <ArgonBox display="flex" alignItems="center" justifyContent="flex-start" gap={4} mb={3}>
          <ArgonTypography variant="body1" fontWeight="medium" color="dark">
            <strong>Tutor&apos;s Name:</strong> {tutorName}
          </ArgonTypography>

          <ArgonBox
            display="flex"
            alignItems="center"
            px={2}
            py={0.5}
            sx={{
              background: getStatusColor(),
              borderRadius: "12px",
              boxShadow: "0 4px 10px rgba(67,160,71,0.25)",
            }}
          >
            <ArgonTypography
              variant="caption"
              fontWeight="bold"
              color="white"
              sx={{ fontSize: "13px", letterSpacing: "0.5px" }}
            >
              {tutorStatus}
            </ArgonTypography>
          </ArgonBox>
        </ArgonBox>

        {/* Cycle Selector */}
        <ArgonBox display="flex" alignItems="center" gap={2}>
          <Dropdown
            label="Select Cycle"
            options={payrollCycles.map((c) => ({
              value: c._id,
              label: `Cycle ${c.cycleNumber} (${c.status})`,
            }))}
            value={cycle}
            onChange={handleCycleChange}
          />
          <ArgonTypography variant="body2" color="text" fontWeight="medium">
            Cycle Type: <strong>{cycleType}</strong>
          </ArgonTypography>
          <ArgonTypography variant="body2" color="text" fontWeight="medium">
            Total Cycles: <strong>{payrollCycles.length}</strong>
          </ArgonTypography>
        </ArgonBox>
      </Card>

      <ArgonBox py={3}>
        <ArgonTypography variant="body1" fontWeight="medium" sx={{ px: 3 }} mb={2}>
          Student List ({totalStudents} Students)
        </ArgonTypography>
        <ArgonBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": {
                borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                  `${borderWidth[1]} solid ${borderColor}`,
              },
            },
          }}
        >
          <Table columns={columns} rows={rows} />
        </ArgonBox>
      </ArgonBox>

      <Card
        sx={{
          mt: 3,
          borderRadius: "16px",
          backgroundColor: "white",
          boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
          border: "1px solid #f0f0f0",
        }}
      >
        <ArgonBox p={3}>
          {/* Header */}
          <ArgonBox display="flex" alignItems="center" gap={2} mb={3}>
            <ArgonBox
              sx={{
                backgroundColor: "white",
                borderRadius: "12px",
                p: 1.2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
              }}
            >
              {/* <Payment sx={{ color: "#344767", fontSize: "22px" }} /> */}
            </ArgonBox>
            <ArgonBox>
              <ArgonTypography variant="h5" fontWeight="bold" color="dark">
                Package
              </ArgonTypography>
              <ArgonTypography variant="body2" color="text">
                Package pricing and tutor salary details
              </ArgonTypography>
            </ArgonBox>
          </ArgonBox>

          <Grid container spacing={3}>
            {/* Hourly Rates */}
            <Grid item xs={12} md={4}>
              <ArgonBox
                sx={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  p: 2.5,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                  border: "1px solid #f5f5f5",
                  height: "100%",
                }}
              >
                <ArgonTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                  Hourly Rates
                </ArgonTypography>
                <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <ArgonTypography variant="body2" color="text" fontWeight="medium">
                    Fee Charge per hour
                  </ArgonTypography>
                  <ArgonTypography variant="body2" fontWeight="medium" color="dark">
                    ₹ {Math.round(feeChargePerHour)}
                  </ArgonTypography>
                </ArgonBox>
                <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <ArgonTypography variant="body2" color="text" fontWeight="medium">
                    Tutor Salary per hour
                  </ArgonTypography>
                  <ArgonTypography variant="body2" fontWeight="medium" color="dark">
                    ₹ {tutorPayRate}
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>
            </Grid>

            {/* Sessions */}
            <Grid item xs={12} md={4}>
              <ArgonBox
                sx={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  p: 2.5,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                  border: "1px solid #f5f5f5",
                  height: "100%",
                }}
              >
                <ArgonTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                  Sessions
                </ArgonTypography>
                <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <ArgonTypography variant="body2" color="text" fontWeight="medium">
                    Sessions in a week
                  </ArgonTypography>
                  <ArgonTypography variant="caption" fontWeight="medium" color="dark">
                    {currentPayrollData?.financeId?.classSessionId?.numberOfClassesInAWeek || 0}{" "}
                    sessions |{" "}
                    {Math.round(
                      ((currentPayrollData?.financeId?.classSessionId?.numberOfClassesInAWeek ||
                        0) *
                        (currentPayrollData?.financeId?.students?.[0]?.preferredTimeDuration ||
                          60)) /
                        60
                    )}{" "}
                    Hours
                  </ArgonTypography>
                </ArgonBox>
                <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                  <ArgonTypography variant="body2" color="text" fontWeight="medium">
                    Sessions in a month
                  </ArgonTypography>
                  <ArgonTypography variant="caption" fontWeight="medium" color="dark">
                    {currentPayrollData?.totalClasses || 0} sessions /{" "}
                    {currentPayrollData?.totalMinutes
                      ? Math.round(currentPayrollData.totalMinutes / 60)
                      : 0}{" "}
                    Hours
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>
            </Grid>

            {/* Cycle Summary - Monthly/Weekly based on cycleType */}
            <Grid item xs={12} md={4}>
              <ArgonBox
                sx={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  p: 2.5,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                  border: "1px solid #f5f5f5",
                  height: "100%",
                }}
              >
                <ArgonTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                  {cycleType === "WEEKLY" ? "Weekly" : "Monthly"} Summary
                </ArgonTypography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <ArgonBox textAlign="center">
                      <ArgonTypography variant="caption" color="text">
                        Charge
                      </ArgonTypography>
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                        ₹ {monthlyCharge}
                      </ArgonTypography>
                    </ArgonBox>
                  </Grid>
                  <Grid item xs={4}>
                    <ArgonBox textAlign="center">
                      <ArgonTypography variant="caption" color="text">
                        Tutor Pay
                      </ArgonTypography>
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                        ₹ {Math.round(expectedTotalTutorPay)}
                      </ArgonTypography>
                      {(bonusAmount > 0 || deductionAmount > 0) && (
                        <ArgonTypography variant="caption" color="text" display="block">
                          +₹{bonusAmount} -₹{deductionAmount}
                        </ArgonTypography>
                      )}
                    </ArgonBox>
                  </Grid>
                  <Grid item xs={4}>
                    <ArgonBox textAlign="center">
                      <ArgonTypography variant="caption" color="text">
                        Profit
                      </ArgonTypography>
                      <ArgonTypography
                        variant="h6"
                        fontWeight="bold"
                        color={monthlyProfit >= 0 ? "success" : "error"}
                      >
                        ₹ {Math.round(monthlyProfit)}
                      </ArgonTypography>
                    </ArgonBox>
                  </Grid>
                </Grid>
              </ArgonBox>
            </Grid>
          </Grid>

          {/* Tutor Info */}
          <ArgonBox mt={3}>
            <ArgonTypography variant="body2" fontWeight="bold" color="dark">
              Tutor Name - {tutorName}
            </ArgonTypography>
            <ArgonTypography variant="caption" color="text">
              Email: {tutorEmail} | Phone: {tutorPhone}
            </ArgonTypography>
          </ArgonBox>
        </ArgonBox>
      </Card>

      <Card
        sx={{
          mt: 3,
          borderRadius: "16px",
          backgroundColor: "white",
          boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
          border: "1px solid #f0f0f0",
        }}
      >
        <ArgonBox p={3}>
          {/* Header */}
          <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <ArgonTypography variant="h5" fontWeight="bold" color="dark">
              Attendance Overview
            </ArgonTypography>
          </ArgonBox>

          {/* Progress Section */}
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} md={5}>
              <ArgonBox>
                {(() => {
                  const completed = Number(currentPayrollData?.completedClasses) || 0;
                  const total = Number(currentPayrollData?.totalClasses) || 0;
                  const percentage = total > 0 ? (completed / total) * 100 : 0;

                  return (
                    <>
                      <ArgonTypography variant="body2" fontWeight="bold" color="text" mb={1}>
                        No. Of Sessions Completed {completed} / {total}
                      </ArgonTypography>

                      {/* Custom Progress Bar */}
                      <ArgonBox
                        sx={{
                          width: "100%",
                          height: 12,
                          backgroundColor: "#f0f0f0",
                          borderRadius: "6px",
                          overflow: "hidden",
                          position: "relative",
                        }}
                      >
                        <ArgonBox
                          sx={{
                            width: `${percentage}%`,
                            height: "100%",
                            backgroundColor: "#1976d2",
                            borderRadius: "6px",
                            transition: "width 0.3s ease",
                          }}
                        />
                      </ArgonBox>

                      <ArgonTypography variant="caption" color="dark" fontWeight="medium" mt={1}>
                        {Math.round(percentage)}%
                      </ArgonTypography>
                    </>
                  );
                })()}
              </ArgonBox>
            </Grid>

            <Grid item xs={12} md={2}></Grid>

            <Grid item xs={12} md={5}>
              <ArgonBox>
                <ArgonTypography variant="body2" fontWeight="bold" color="text" mb={1}>
                  Remaining Sessions
                </ArgonTypography>
                <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                  {(currentPayrollData?.totalClasses || 0) -
                    (currentPayrollData?.completedClasses || 0)}
                </ArgonTypography>
              </ArgonBox>
            </Grid>
          </Grid>

          {/* Expiry Warning */}
          {(() => {
            const remaining = totalClasses - completedClasses;
            const thresholdSessions = Math.min(5, totalClasses);
            const sessionsNeeded = Math.max(0, thresholdSessions - completedClasses);

            if (completedClasses >= thresholdSessions) {
              return (
                <ArgonBox
                  mt={2}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    width: { xs: "100%", md: "50%" },
                    margin: "16px auto 0",
                    border: "2px solid #4caf50",
                    borderRadius: "12px",
                    p: 1,
                    backgroundColor: "#f0fff4",
                  }}
                >
                  <i
                    className="fa-solid fa-check-circle"
                    style={{ color: "green", fontSize: "22px", marginRight: "12px" }}
                  ></i>
                  <ArgonTypography variant="body2" fontWeight="bold" color="dark">
                    {"Tutor's salary is now due for payment. ₹"}
                    {Math.round(tutorPayEarned)}
                    {" earned so far."}
                  </ArgonTypography>
                </ArgonBox>
              );
            }

            return (
              <ArgonBox
                mt={2}
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                  width: { xs: "100%", md: "50%" },
                  margin: "16px auto 0",
                  border: "2px solid #f44336",
                  borderRadius: "12px",
                  p: 1,
                  backgroundColor: "#fff5f5",
                }}
              >
                <i
                  className="fa-solid fa-triangle-exclamation"
                  style={{ color: "red", fontSize: "22px", marginRight: "12px" }}
                ></i>
                <ArgonTypography variant="body2" fontWeight="bold" color="dark">
                  {`Tutor's salary will be due after ${sessionsNeeded} more session${
                    sessionsNeeded !== 1 ? "s" : ""
                  } are completed.`}
                </ArgonTypography>
              </ArgonBox>
            );
          })()}
        </ArgonBox>
      </Card>

      <Card
        sx={{
          mt: 3,
          borderRadius: "20px",
          backgroundColor: "white",
          boxShadow: "0 15px 35px rgba(0,0,0,0.08)",
          border: "1px solid #f0f0f0",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative Background Element */}
        <ArgonBox
          sx={{
            position: "absolute",
            top: -40,
            right: -40,
            width: 120,
            height: 120,
            background: "linear-gradient(45deg, #667eea, #764ba2)",
            borderRadius: "50%",
            opacity: 0.05,
          }}
        />

        <ArgonBox p={4}>
          {/* Header */}
          <ArgonBox display="flex" alignItems="center" gap={2} mb={4}>
            <ArgonBox
              sx={{
                background: "linear-gradient(45deg, #667eea, #764ba2)",
                borderRadius: "12px",
                p: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
              }}
            >
              {/* <AccountBalance sx={{ color: "white", fontSize: "22px" }} /> */}
            </ArgonBox>
            <ArgonBox>
              <ArgonTypography variant="h5" fontWeight="bold" color="#2c3e50">
                Credit Counter
              </ArgonTypography>
              <ArgonTypography variant="body2" color="#6c757d">
                Track credits, usage and payments
              </ArgonTypography>
            </ArgonBox>
          </ArgonBox>

          <Grid container spacing={4}>
            {/* Progress Bar Section */}
            <Grid item xs={12} md={6}>
              <ArgonBox
                sx={{
                  background: "#f8f9fa",
                  borderRadius: "16px",
                  p: 3,
                  textAlign: "center",
                  border: "1px solid #e9ecef",
                }}
              >
                <ArgonBox
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                  mb={3}
                  justifyContent="center"
                >
                  {/* <DonutLarge sx={{ color: "#2196F3", fontSize: "20px" }} /> */}
                  <ArgonTypography variant="h6" fontWeight="bold" color="#2c3e50">
                    Usage Progress
                  </ArgonTypography>
                </ArgonBox>

                <ArgonBox
                  sx={{
                    width: 180,
                    height: 180,
                    margin: "0 auto",
                    mb: 2,
                  }}
                >
                  <svg viewBox="0 0 100 100" style={{ width: "100%", height: "100%" }}>
                    <path
                      d="M 50,50 m 0,-46 a 46,46 0 1 1 0,92 a 46,46 0 1 1 0,-92"
                      strokeWidth="6"
                      fillOpacity="0"
                      style={{ stroke: "#e0e0e0" }}
                    />
                    <path
                      d="M 50,50 m 0,-46 a 46,46 0 1 1 0,92 a 46,46 0 1 1 0,-92"
                      strokeWidth="6"
                      fillOpacity="0"
                      style={{
                        stroke: "url(#gradient)",
                        strokeDasharray: "289.027px, 289.027px",
                        strokeDashoffset: `${
                          289.027 - (289.027 * classProgressPercentage) / 100
                        }px`,
                        strokeLinecap: "round",
                      }}
                    />
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" style={{ stopColor: "#667eea", stopOpacity: 1 }} />
                        <stop offset="100%" style={{ stopColor: "#764ba2", stopOpacity: 1 }} />
                      </linearGradient>
                    </defs>
                    <text
                      x="50"
                      y="45"
                      textAnchor="middle"
                      alignmentBaseline="central"
                      style={{ fill: "#2c3e50", fontSize: "12px", fontWeight: "bold" }}
                    >
                      {classProgressPercentage}%
                    </text>
                    <text
                      x="50"
                      y="58"
                      textAnchor="middle"
                      alignmentBaseline="central"
                      style={{ fill: "#6c757d", fontSize: "6px" }}
                    >
                      Completed
                    </text>
                  </svg>
                </ArgonBox>

                <ArgonBox>
                  <ArgonTypography variant="h5" fontWeight="bold" color="#2c3e50">
                    ₹{Math.round(tutorPayEarned)} / ₹{Math.round(expectedTotalTutorPay)}
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="#6c757d">
                    Tutor Pay Progress
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="success.main" fontWeight="medium">
                    {completedClasses} / {totalClasses} Classes Completed
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>
            </Grid>

            {/* Table Section */}
            <Grid item xs={12} md={6}>
              <ArgonBox
                sx={{
                  background: "white",
                  borderRadius: "16px",
                  p: 3,
                  boxShadow: "0 8px 25px rgba(0,0,0,0.06)",
                  border: "1px solid #f0f0f0",
                }}
              >
                <ArgonBox display="flex" alignItems="center" gap={1.5} mb={3}>
                  {/* <TableChart sx={{ color: "#4CAF50", fontSize: "20px" }} /> */}
                  <ArgonTypography variant="h6" fontWeight="bold" color="#2c3e50">
                    Credit Details
                  </ArgonTypography>
                </ArgonBox>

                <ArgonBox
                  sx={{
                    "& .MuiTableRow-root:not(:last-child)": {
                      "& td": {
                        borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                          `${borderWidth[1]} solid ${borderColor}`,
                      },
                    },
                  }}
                >
                  <Table columns={creditColumns} rows={creditRows} />
                </ArgonBox>
              </ArgonBox>
            </Grid>
          </Grid>

          <Grid container spacing={3} mt={2} alignItems="stretch">
            {/* Account Summary */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: "100%",
                  borderRadius: "16px",
                  backgroundColor: "white",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                  border: "1px solid #f0f0f0",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <ArgonBox p={3} flexGrow={1}>
                  <ArgonTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                    Account Summary
                  </ArgonTypography>

                  <ArgonBox display="flex" justifyContent="space-between" mb={1.5}>
                    <ArgonTypography variant="body2" color="text" fontWeight="medium">
                      Balance:
                    </ArgonTypography>
                    <ArgonTypography variant="body2" fontWeight="bold" color="primary">
                      ₹{(currentPayrollData?.financeId?.totalAmount || 0) - (finalAmount || 0)}
                    </ArgonTypography>
                  </ArgonBox>

                  <ArgonBox display="flex" justifyContent="space-between" mb={1.5}>
                    <ArgonTypography variant="body2" color="text" fontWeight="medium">
                      Total Classes:
                    </ArgonTypography>
                    <Chip
                      label={currentPayrollData?.totalClasses || 0}
                      size="small"
                      sx={{
                        bgcolor: "primary.main",
                        color: "white",
                        fontWeight: "600",
                      }}
                    />
                  </ArgonBox>

                  <ArgonBox display="flex" justifyContent="space-between">
                    <ArgonTypography variant="body2" color="text" fontWeight="medium">
                      Usually Pays By:
                    </ArgonTypography>
                    <Chip
                      label={
                        currentPayrollData?.financeId?.paymentType ||
                        currentPayrollData?.paymentType ||
                        "UPI"
                      }
                      size="small"
                      sx={{
                        bgcolor: "success.main",
                        color: "white",
                        fontWeight: "600",
                      }}
                    />
                  </ArgonBox>
                </ArgonBox>
              </Card>
            </Grid>

            {/* Payment Details */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: "100%",
                  borderRadius: "16px",
                  backgroundColor: "white",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                  border: "1px solid #f0f0f0",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <ArgonBox p={3} flexGrow={1}>
                  <ArgonTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                    Payment Details
                  </ArgonTypography>

                  <ArgonBox display="flex" justifyContent="space-between" mb={1.5}>
                    <ArgonTypography variant="body2" color="text" fontWeight="medium">
                      Base Amount:
                    </ArgonTypography>
                    <ArgonTypography variant="body2" fontWeight="bold" color="dark">
                      ₹{currentPayrollData?.calculatedAmount || 0}
                    </ArgonTypography>
                  </ArgonBox>

                  <ArgonBox display="flex" justifyContent="space-between" mb={1.5}>
                    <ArgonTypography variant="body2" color="text" fontWeight="medium">
                      Bonus Amount:
                    </ArgonTypography>
                    <Chip
                      label={`₹${currentPayrollData?.bonusAmount || 0}`}
                      size="small"
                      sx={{
                        bgcolor: "success.main",
                        color: "white",
                        fontWeight: "600",
                      }}
                    />
                  </ArgonBox>

                  <ArgonBox display="flex" justifyContent="space-between" mb={1.5}>
                    <ArgonTypography variant="body2" color="text" fontWeight="medium">
                      Deduction:
                    </ArgonTypography>
                    <ArgonTypography variant="body2" fontWeight="bold" color="error.dark">
                      ₹{currentPayrollData?.deductionAmount || 0}
                    </ArgonTypography>
                  </ArgonBox>

                  <ArgonBox
                    display="flex"
                    justifyContent="space-between"
                    sx={{
                      borderTop: "2px solid #e0e0e0",
                      pt: 1.5,
                      mt: 1,
                    }}
                  >
                    <ArgonTypography variant="body2" color="text" fontWeight="bold">
                      Final Amount:
                    </ArgonTypography>
                    <ArgonTypography variant="h6" fontWeight="bold" color="success.main">
                      ₹{finalAmount}
                    </ArgonTypography>
                  </ArgonBox>
                </ArgonBox>
              </Card>
            </Grid>

            {/* Cycle Summary */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: "100%",
                  borderRadius: "16px",
                  backgroundColor: "white",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                  border: "1px solid #f0f0f0",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <ArgonBox p={3} flexGrow={1}>
                  <ArgonTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                    Cycle Summary
                  </ArgonTypography>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <ArgonBox display="flex" justifyContent="space-between">
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Cycle Type:
                        </ArgonTypography>
                        <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                          {currentPayrollData?.cycleType || "N/A"}
                        </ArgonTypography>
                      </ArgonBox>
                    </Grid>

                    <Grid item xs={12}>
                      <ArgonBox display="flex" justifyContent="space-between">
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Total Minutes:
                        </ArgonTypography>
                        <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                          {currentPayrollData?.totalMinutes || 0} mins
                        </ArgonTypography>
                      </ArgonBox>
                    </Grid>

                    <Grid item xs={12}>
                      <ArgonBox display="flex" justifyContent="space-between">
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Final Amount:
                        </ArgonTypography>
                        <ArgonTypography variant="h6" fontWeight="bold" color="success.main">
                          ₹ {finalAmount}
                        </ArgonTypography>
                      </ArgonBox>
                    </Grid>
                  </Grid>
                </ArgonBox>
              </Card>
            </Grid>
          </Grid>

          {/* <ArgonBox mt={4}>
            <ArgonTypography mb={2} variant="body2" fontWeight="medium" color="dark">
              Total Payment Orders
            </ArgonTypography>

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              <Table columns={columns2} rows={rows2} />
            </ArgonBox>
          </ArgonBox> */}
        </ArgonBox>
      </Card>
    </ArgonBox>
  );
}

payroll.propTypes = {
  classData: PropTypes.shape({
    students: PropTypes.array,
    tutor: PropTypes.shape({
      name: PropTypes.string,
      fullName: PropTypes.string,
      email: PropTypes.string,
      phone: PropTypes.string,
    }),
    parent: PropTypes.shape({
      city: PropTypes.string,
    }),
    cycles: PropTypes.array,
    status: PropTypes.string,
    cycleType: PropTypes.string,
    calculatedAmount: PropTypes.number,
    bonusAmount: PropTypes.number,
    deductionAmount: PropTypes.number,
    monthlyTutorSalary: PropTypes.number,
    completedClasses: PropTypes.number,
    totalNoOfClasses: PropTypes.number,
    totalClasses: PropTypes.number,
    feeCharge: PropTypes.number,
    tutorPayRate: PropTypes.number,
    tutorSalary: PropTypes.number,
    paymentAmount: PropTypes.number,
    numberOfClassesInAWeek: PropTypes.number,
    weeklyClasses: PropTypes.number,
    duration: PropTypes.number,
    noOfMonths: PropTypes.number,
    totalMinutes: PropTypes.number,
    paymentType: PropTypes.string,
  }),
  payrollCycles: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      tutorId: PropTypes.shape({
        _id: PropTypes.string,
        fullName: PropTypes.string,
        email: PropTypes.string,
        phone: PropTypes.string,
      }),
      financeId: PropTypes.shape({
        _id: PropTypes.string,
        parentId: PropTypes.shape({
          _id: PropTypes.string,
          city: PropTypes.string,
        }),
        students: PropTypes.array,
        classSessionId: PropTypes.shape({
          _id: PropTypes.string,
          numberOfClassesInAWeek: PropTypes.number,
        }),
        totalAmount: PropTypes.number,
        tutorPayRate: PropTypes.number,
        paymentType: PropTypes.string,
      }),
      cycleNumber: PropTypes.number,
      cycleStartDate: PropTypes.string,
      cycleEndDate: PropTypes.string,
      totalClasses: PropTypes.number,
      completedClasses: PropTypes.number,
      totalMinutes: PropTypes.number,
      calculatedAmount: PropTypes.number,
      bonusAmount: PropTypes.number,
      deductionAmount: PropTypes.number,
      status: PropTypes.string,
      cycleType: PropTypes.string,
    })
  ),
};

export default payroll;
